import React, { useContext } from 'react';

import HeaderTSX from '@/client/components/Header/Header';
import { linkGeneratorHelper, generateLinkObject } from '@/utils/links/linkGeneratorHelper';
import getClientPageQueryObject from '@/client/utils/getClientPageQueryObject';
import pushToRouter from '@/client/utils/pushToRouter';
import removeSavedSearchFlag from '@/client/utils/searchRequestUtils';
import useIsMarktplaats from '@/client/hooks/useIsMarktplaats';
import enrichHeaderConfig from '@/client/components/Header/utils/headerConfig';
import getCategoryFromId from '@/client/components/Header/utils/getCategoryFromId';
import isRemoveAllAttributes from '@/client/components/Header/utils/isRemoveAllAttributes';
import EnvironmentContext from '@/client/contexts/EnvironmentContext';
import experiments from '@/utils/labs/experiments';
import trackEvent from '@/client/utils/analytics/trackEvent';
import CATEGORIES_L1 from '@/constants/categories';

// quick fix for prod issue.
// TODO: refinement needed.
export const getPromotionalHeader = (
  is25YearsEnabled: boolean,
  tenant: string,
  logoUrls: any,
  anniversaryLogosPath: any,
) => {
  const isMarktplaats = tenant === 'mp';
  return is25YearsEnabled && isMarktplaats ? anniversaryLogosPath : logoUrls;
};

const Header = (props: THeaderProperties) => {
  const { labsConfig } = useContext(EnvironmentContext);
  const {
    isSticky = false,
    showNavBar = true,
    navBar,
    showSearchBar,
    searchBar,
    searchCategoryId,
    searchCategoryOptions,
    searchRequestObject,
    reloadPageOnSearch,
    experiments: experimentData,
    xsrfToken,
    logoUrls,
  } = props;

  const {
    isFetchResultsOnceEnabled,
    isFetchResultsOnceAllCatEnabled,
    isFetchResultsHIOnceEnabled,
    isAttributeSuggestionTooltipEnabled,
  } = experiments({
    labsConfig,
  });

  const isMarktplaats = useIsMarktplaats();
  const withAllAttributes =
    isFetchResultsOnceAllCatEnabled ||
    (isFetchResultsOnceEnabled && searchRequestObject.categories?.l1Category?.id === CATEGORIES_L1.DAMES_KLEREN) ||
    (isFetchResultsHIOnceEnabled &&
      isMarktplaats &&
      searchRequestObject.categories?.l1Category?.id === CATEGORIES_L1.HUIS_EN_INRICHTING);

  React.useEffect(() => {
    const handleSearch = (e) => {
      e.preventDefault();

      const { seller, categories: SROCategory } = searchRequestObject;

      const { category: categoryId, distance, postcode, query: eventQuery, onPage: isSearchOnPage } = e.detail;

      const query = eventQuery || searchRequestObject.searchQuery || '';

      const searchCategory = getCategoryFromId(searchCategoryOptions, categoryId);
      const { l1Category, l2Category } = searchCategory;
      const isEmptyQuery = !query || query.length === 0;
      const isSiteWideSearch = !l2Category && !l1Category;
      const isEmptySearch = isEmptyQuery && isSiteWideSearch && !seller;

      if (isEmptySearch) {
        window.location.assign('/');
        return;
      }

      const currentL1Id = searchRequestObject.categories?.l1Category?.id || null;
      const currentL2Id = searchRequestObject.categories?.l2Category?.id || null;
      const newL1Id = l1Category?.id || null;
      const newL2Id = l2Category?.id || null;
      const isChangingCategory = newL1Id !== currentL1Id || newL2Id !== currentL2Id;
      const currentQuery = searchRequestObject.searchQuery || '';
      const isQueryChanged = query !== currentQuery;
      const currentDistanceNum = searchRequestObject?.distance?.distanceMeters || 0;
      const newDistanceNum = distance ? parseInt(distance, 10) : 0;
      const isDistanceChanged = currentDistanceNum !== newDistanceNum;
      const currentPostcode = searchRequestObject?.distance?.postcode || '';
      const isPostcodeChanged = postcode !== currentPostcode;

      const hasChanges = isQueryChanged || isChangingCategory || isDistanceChanged || isPostcodeChanged;
      const hasQueryRequiringReload = hasChanges && query && !searchRequestObject.asSavedSearch;

      const options: any = {
        query,
        categories: searchCategory,
        postcode,
        distance,
        seller: isSearchOnPage ? seller : {}, // TODO VOS-6871
        removeAllAttributes:
          isChangingCategory && currentL2Id && isAttributeSuggestionTooltipEnabled
            ? true
            : isRemoveAllAttributes(searchCategory, SROCategory),
        removeAllFilters: false,
        searchRequestObject: removeSavedSearchFlag(searchRequestObject),
        withAllAttributes,
        isSuggestedSearch: hasQueryRequiringReload && !!query,
        sortOptions:
          isDistanceChanged && distance === '0' && searchRequestObject?.sortOptions?.sortBy === 'LOCATION'
            ? { sortBy: '', sortOrder: '', sortAttribute: '' }
            : searchRequestObject.sortOptions,
      };

      let href = linkGeneratorHelper(options);

      if (isMarktplaats && isQueryChanged && query) {
        href = href.includes('#') ? href.replace('#', '?trackSearch=1#') : `${href}?trackSearch=1`;
      }

      if (hasQueryRequiringReload || reloadPageOnSearch || !!seller !== !!isSearchOnPage) {
        if (hasQueryRequiringReload && window.location.pathname === new URL(href, window.location.origin).pathname) {
          window.location.assign(href);
          window.location.reload();
        } else {
          window.location.assign(href);
        }
      } else if (hasChanges) {
        const pageQuery = getClientPageQueryObject(generateLinkObject(options));
        pushToRouter({
          query: pageQuery,
          href,
        });
      }
    };

    const node = document.getElementsByTagName('body')[0];
    node.addEventListener('search', handleSearch);

    return () => {
      node.removeEventListener('search', handleSearch);
    };
  }, [
    searchRequestObject,
    searchCategoryOptions,
    reloadPageOnSearch,
    withAllAttributes,
    isAttributeSuggestionTooltipEnabled,
    isMarktplaats,
  ]);

  const headerData: THeaderProperties = {
    isSticky,
    showSearchBar,
    showNavBar,
    navBar,
    searchBar,
    searchCategoryId,
    searchCategoryOptions,
    searchRequestObject,
    xsrfToken,
    experiments: experimentData,
    logoUrls,
  };

  const headerConfig = enrichHeaderConfig(headerData);

  const handleTrackGAEvent = (eventName: string, eventAction: string) => {
    trackEvent({
      eventAction,
      eventLabel: eventName,
    });
  };

  return <HeaderTSX headerConfig={headerConfig} trackGAEvent={handleTrackGAEvent} />;
};

export default Header;
